import React, { useState } from 'react';

function formatM(n) {
  return '$' + (n / 1000000).toFixed(1) + 'M';
}

function formatFull(n) {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function StockChart({ data, width = 960, height = 440, color, events }) {
  const [hover, setHover] = useState(null);

  const FIRST = data[0].value;
  const LAST = data[data.length - 1].value;
  const IS_UP = LAST >= FIRST;
  const PRIMARY = color || (IS_UP ? '#4ade80' : '#f87171');

  const values = data.map((d) => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  const padL = 80;
  const padR = 40;
  const padT = 30;
  const padB = 50;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;

  const minV = minVal * 0.92;
  const maxV = maxVal * 1.04;
  const rangeV = maxV - minV;

  const getX = (i) => padL + (i / (data.length - 1)) * chartW;
  const getY = (v) => padT + (1 - (v - minV) / rangeV) * chartH;

  const linePoints = data.map((d, i) => `${getX(i)},${getY(d.value)}`).join(' ');
  const areaPoints = `${getX(0)},${padT + chartH} ${linePoints} ${getX(data.length - 1)},${padT + chartH}`;

  const gridCount = 6;
  const gridLines = [];
  for (let i = 0; i <= gridCount; i++) {
    const v = minV + (rangeV * i) / gridCount;
    gridLines.push({ y: getY(v), label: formatM(v) });
  }

  const yoy = data.length >= 2
    ? ((data[data.length - 1].value - data[data.length - 2].value) / data[data.length - 2].value) * 100
    : 0;
  const totalChange = ((LAST - FIRST) / FIRST) * 100;

  return (
    <div className="stock-chart-wrapper">
      <div className="stock-stats">
        <div className="stock-stat-main">
          <span className="stock-stat-value" style={{ color: PRIMARY }}>{formatFull(LAST)}</span>
          <span className="stock-stat-year">2025</span>
        </div>
        <div className="stock-stat-pill" style={{ background: `${PRIMARY}15`, color: PRIMARY, borderColor: `${PRIMARY}40` }}>
          {totalChange >= 0 ? '+' : ''}{totalChange.toFixed(1)}% vs 2010
        </div>
        <div className="stock-stat-pill" style={{ background: `${yoy >= 0 ? '#4ade80' : '#f87171'}15`, color: yoy >= 0 ? '#4ade80' : '#f87171', borderColor: `${yoy >= 0 ? '#4ade80' : '#f87171'}40` }}>
          {yoy >= 0 ? '+' : ''}{yoy.toFixed(1)}% YoY
        </div>
        <div className="stock-stat-range">
          <span className="stock-stat-lo">Min: {formatM(minVal)}</span>
          <span className="stock-stat-hi">Max: {formatM(maxVal)}</span>
        </div>
      </div>

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="stock-svg"
        onMouseLeave={() => setHover(null)}>
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={padL} y1={g.y} x2={width - padR} y2={g.y} className="grid-line" />
            <text x={padL - 10} y={g.y} textAnchor="end" dominantBaseline="middle" className="grid-label">{g.label}</text>
          </g>
        ))}
        {data.map((d, i) => (
          <text key={d.year} x={getX(i)} y={padT + chartH + 25} textAnchor="middle" className="year-label">{d.year}</text>
        ))}
        <defs>
          <linearGradient id={`area-grad-${PRIMARY.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.25" />
            <stop offset="100%" stopColor={PRIMARY} stopOpacity="0.02" />
          </linearGradient>
          <filter id={`glow-${PRIMARY.replace('#','')}`}>
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={PRIMARY} floodOpacity="0.5" />
          </filter>
        </defs>
        <polygon points={areaPoints} fill={`url(#area-grad-${PRIMARY.replace('#','')})`} />
        <polyline points={linePoints} fill="none" stroke={PRIMARY} strokeWidth="3"
          strokeLinecap="round" strokeLinejoin="round" filter={`url(#glow-${PRIMARY.replace('#','')})`} />
        {data.map((d, i) => (
          <circle key={d.year} cx={getX(i)} cy={getY(d.value)}
            r={hover === i ? 7 : 4}
            fill={hover === i ? '#fff' : PRIMARY}
            stroke={hover === i ? PRIMARY : 'rgba(15,23,42,0.8)'}
            strokeWidth={hover === i ? 3 : 2}
            className="data-dot" />
        ))}
        {data.map((d, i) => (
          <rect key={`hz-${d.year}`}
            x={getX(i) - chartW / data.length / 2} y={padT}
            width={chartW / data.length} height={chartH}
            fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
        {/* Event markers */}
        {events && events.map((evt, ei) => {
          const idx = data.findIndex((d) => d.year === evt.year);
          if (idx === -1) return null;
          const ex = getX(idx);
          const ey = getY(data[idx].value);
          // Alternate label position above/below to avoid overlap
          const above = ei % 2 === 0;
          const labelY = above ? padT + 14 : padT + chartH - 8;
          return (
            <g key={`evt-${evt.year}`} className="event-marker">
              <line x1={ex} y1={padT} x2={ex} y2={padT + chartH} className="event-line" />
              <circle cx={ex} cy={ey} r="6" className="event-dot" />
              <rect x={ex - 2} y={labelY - 10} width={evt.label.length * 5.8 + 14} height="20" rx="4"
                className="event-label-bg"
                transform={`translate(${-((evt.label.length * 5.8 + 14) / 2) + 2}, 0)`} />
              <text x={ex} y={labelY + 1} textAnchor="middle" dominantBaseline="middle" className="event-label-text">
                {evt.label}
              </text>
            </g>
          );
        })}
        {hover !== null && (
          <g>
            <line x1={getX(hover)} y1={padT} x2={getX(hover)} y2={padT + chartH} className="crosshair" />
            <line x1={padL} y1={getY(data[hover].value)} x2={width - padR} y2={getY(data[hover].value)} className="crosshair-h" />
            <rect x={getX(hover) - 52} y={getY(data[hover].value) - 36}
              width="104" height="28" rx="6"
              fill="rgba(15,23,42,0.95)" stroke={PRIMARY} strokeWidth="1" />
            <text x={getX(hover)} y={getY(data[hover].value) - 22}
              textAnchor="middle" dominantBaseline="middle"
              className="tooltip-text" fill={PRIMARY}>
              {data[hover].year}: {formatM(data[hover].value)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

export default StockChart;
